import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Alert } from "react-bootstrap";
import moment from "moment";
import { instanceMob } from "../../../../utils/utils";
import InfoModal from "../../../info-modal/infoModal";
import "./biometric-capture-modal.css";

const BiometricCaptureModal = ({ show, onClose, user }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [stream, setStream] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState(null);
    const [isCapturing, setIsCapturing] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [storedPhoto, setStoredPhoto] = useState(null);
    const [isLoadingPhoto, setIsLoadingPhoto] = useState(false);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoModalData, setInfoModalData] = useState({ title: "", description: "", iconType: "success", primaryAction: null, secondaryAction: null });
    const [showFullHistory, setShowFullHistory] = useState(false);
    const streamRef = useRef(null); // Used for robust cleanup independent of closures
    const abortControllerRef = useRef(null);

    // Registered locations states
    const [registeredLocations, setRegisteredLocations] = useState([]);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);

    // New states for device capture
    const [captureMode, setCaptureMode] = useState("webcam"); // 'webcam' or 'device'
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState("");
    const [isFetchingDevices, setIsFetchingDevices] = useState(false);
    const [isCameraLoading, setIsCameraLoading] = useState(false);

    const startCamera = async () => {
        if (isCameraLoading || streamRef.current) return;

        setIsCameraLoading(true);
        setError(null);
        abortControllerRef.current = new AbortController();
        const currentAbortController = abortControllerRef.current;

        try {
            // Extremly simple constraints for fastest possible initialization
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user" }
            });

            // "Loading latch": If we started stopping while this was opening, discard the stream immediately
            if (currentAbortController.signal.aborted) {
                newStream.getTracks().forEach(track => track.stop());
                return;
            }

            setStream(newStream);
            streamRef.current = newStream;
        } catch (err) {
            console.error("Error accessing camera:", err);
            setError("No se pudo acceder a la cámara. Verifique los permisos o si está siendo usada por otra aplicación.");
        } finally {
            setIsCameraLoading(false);
            if (abortControllerRef.current === currentAbortController) {
                abortControllerRef.current = null;
            }
        }
    };

    const stopCamera = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        setStream(null);
    };

    const fetchCurrentPhoto = async () => {
        if (!user?.customer_id) return;
        setIsLoadingPhoto(true);
        try {
            const res = await instanceMob.get(`/biometrics/info/customer/photo?customer_id=${user.customer_id}&t=${Date.now()}`, {
                responseType: 'blob'
            });

            if (res.data) {
                if (storedPhoto && storedPhoto.startsWith('blob:')) {
                    URL.revokeObjectURL(storedPhoto);
                }
                const imageUrl = URL.createObjectURL(res.data);
                setStoredPhoto(imageUrl);
            }
        } catch (err) {
            console.error("Error fetching current photo:", err);
            setStoredPhoto(null);
        } finally {
            setIsLoadingPhoto(false);
            setInitialFetchDone(true);
        }
    };

    const fetchHistory = async () => {
        if (!user?.customer_id) return;
        setIsLoadingHistory(true);
        try {
            const res = await instanceMob.get(`/biometrics/customer/logs?customer=${user.customer_id}`);
            // Handle paginated response structure
            const historyData = res.data?.results || res.data || [];
            const mappedData = (Array.isArray(historyData) ? historyData : []).map(x => ({
                id: x.id,
                customer_id: x.customer_id,
                biometric_id: x.biometric_id,
                location: x.location,
                is_allowed: x.is_allowed,
                date: x.access_date ? moment(x.access_date, "DD/MM/YYYY hh:mm:ss A").format("DD/MM/YYYY HH:mm") : "N/A",
                status: x.is_allowed ? "Permitido" : "Denegado",
                thumb: null // Keep null for now as not provided in mapping
            }));
            setHistory(mappedData);
        } catch (err) {
            console.error("Error fetching history:", err);
            setHistory([]);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const fetchDevices = async () => {
        setIsFetchingDevices(true);
        try {
            const res = await instanceMob.get(`/biometrics`);
            const data = Array.isArray(res.data) ? res.data : res.data?.results || [];

            // Filter only online and enabled devices
            const activeDevices = data.filter(dev => dev.current_status === 10 && dev.enable);

            setDevices(activeDevices);
            if (activeDevices.length > 0 && !selectedDeviceId) {
                setSelectedDeviceId(activeDevices[0].id);
            }
        } catch (err) {
            console.error("Error fetching biometric devices:", err);
        } finally {
            setIsFetchingDevices(false);
        }
    };

    const fetchRegisteredLocations = async () => {
        if (!user?.customer_id) return;
        setIsLoadingLocations(true);
        try {
            const res = await instanceMob.get(`/biometrics/info/customer/?customer_id=${user.customer_id}`);
            const data = res.data?.locations || [];
            setRegisteredLocations(data);
        } catch (err) {
            console.error("Error fetching registered locations:", err);
            setRegisteredLocations([]);
        } finally {
            setIsLoadingLocations(false);
        }
    };

    const handleDeleteFromLocation = async (locationId) => {
        setError(null);
        try {
            await instanceMob.delete(`/biometrics/info/customer/delete/location`, {
                data: {
                    customer_id: user.customer_id,
                    location_ids: [locationId]
                }
            });

            // Refresh locations after delete
            await fetchRegisteredLocations();

            setInfoModalData({
                title: "Registro Removido",
                description: "El usuario ha sido removido de la locación seleccionada.",
                iconType: "success",
                primaryAction: {
                    label: "Entendido",
                    onClick: () => setShowInfoModal(false)
                }
            });
            setShowInfoModal(true);
        } catch (err) {
            console.error("Error deleting from location:", err);
            setError("Error al eliminar el registro de la locación. Intente de nuevo.");
        }
    };

    const confirmDeleteFromLocation = (location) => {
        setInfoModalData({
            title: "¿Remover de Locación?",
            description: `¿Desea remover el registro biométrico de ${user.customer_name} en "${location.name}"?`,
            iconType: "warning",
            primaryAction: {
                label: "Remover",
                onClick: async () => {
                    setShowInfoModal(false);
                    await handleDeleteFromLocation(location.id);
                }
            },
            secondaryAction: {
                label: "Cancelar",
                onClick: () => setShowInfoModal(false)
            }
        });
        setShowInfoModal(true);
    };

    const handleDeviceSnapshot = async () => {
        if (!selectedDeviceId) {
            setError("Por favor, seleccione un dispositivo.");
            return;
        }

        setIsCapturing(true);
        setError(null);
        try {
            const payload = {
                biometric_id: selectedDeviceId
            };

            // Snapshot returns the actual image as a blob
            const response = await instanceMob.post(`/biometrics/snapshot`, payload, {
                responseType: 'blob'
            });

            // Convert Blob to Base64 to store it via the existing upload function
            const reader = new FileReader();
            reader.readAsDataURL(response.data);
            reader.onloadend = async () => {
                const base64data = reader.result;
                // resolution doesn't apply strictly for device capture here, but we pass a label
                await uploadBiometric(base64data, 'device_capture');
            };

        } catch (err) {
            console.error("Error triggering snapshot:", err);
            setError("Error al enviar la orden al dispositivo o al procesar la imagen. Verifique que esté en línea.");
            setIsCapturing(false);
        }
    };

    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");

        // Validate resolution
        const width = video.videoWidth;
        const height = video.videoHeight;

        // if (width < 1280 || height < 720) {
        //     setError(`La resolución actual (${width}x${height}) es menor a 720p. Por favor, ajuste su cámara.`);
        //     return;
        // }

        canvas.width = width;
        canvas.height = height;

        // Mirror the capture to match preview
        context.translate(width, 0);
        context.scale(-1, 1);
        context.drawImage(video, 0, 0, width, height);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        uploadBiometric(dataUrl, `${width}x${height}`);
    };

    const uploadBiometric = async (base64Image, resolution) => {
        setIsCapturing(true);
        setError(null);
        try {
            const payload = {
                customer_id: user.customer_id,
                image_base64: base64Image
            };

            await instanceMob.post(`/biometrics/info/customer/create`, payload);

            // Wait a moment for backend processing
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Refresh photo and history after success
            await Promise.all([
                fetchHistory(),
                fetchCurrentPhoto(),
                fetchRegisteredLocations()
            ]);

            setInfoModalData({
                title: "¡Éxito!",
                description: "La información biométrica ha sido registrada correctamente.",
                iconType: "success",
                primaryAction: {
                    label: "Entendido",
                    onClick: () => setShowInfoModal(false)
                }
            });
            setShowInfoModal(true);
        } catch (err) {
            console.error("Error uploading biometric:", err);
            if (err.response && err.response.data) {
                const data = err.response.data;
                if (data.image_base64) {
                    setError(data.image_base64);
                } else {
                    setError("Error al registrar la información biométrica. Intente de nuevo.");
                }
            } else {
                setError("Error al registrar la información biométrica. Intente de nuevo.");
            }
        } finally {
            setIsCapturing(false);
        }
    };

    const deleteBiometric = async () => {
        setError(null);
        try {
            await instanceMob.delete(`/biometrics/info/customer/delete`, {
                data: {
                    customer_id: user.customer_id
                }
            });

            if (storedPhoto && storedPhoto.startsWith('blob:')) {
                URL.revokeObjectURL(storedPhoto);
            }
            setStoredPhoto(null);
            await fetchHistory();

            setInfoModalData({
                title: "Registro Eliminado",
                description: "La información biométrica ha sido eliminada correctamente.",
                iconType: "success",
                primaryAction: {
                    label: "Entendido",
                    onClick: () => setShowInfoModal(false)
                }
            });
            setShowInfoModal(true);
        } catch (err) {
            console.error("Error deleting biometric:", err);
            setError("Error al eliminar el registro biométrico. Intente de nuevo.");
        }
    };

    const confirmDelete = () => {
        setInfoModalData({
            title: "¿Eliminar Registro?",
            description: "Esta acción eliminará permanentemente la información biométrica del usuario. ¿Desea continuar?",
            iconType: "warning",
            primaryAction: {
                label: "Sí, Eliminar",
                onClick: async () => {
                    await deleteBiometric();
                }
            },
            secondaryAction: {
                label: "Cancelar",
                onClick: () => setShowInfoModal(false)
            }
        });
        setShowInfoModal(true);
    };

    useEffect(() => {
        if (show) {
            setInitialFetchDone(false);
            setShowFullHistory(false);
            fetchHistory();
            fetchCurrentPhoto();
            fetchRegisteredLocations();
        } else {
            stopCamera();
            if (storedPhoto && storedPhoto.startsWith('blob:')) {
                URL.revokeObjectURL(storedPhoto);
            }
            setStoredPhoto(null);
            setInitialFetchDone(false);
        }
        return () => {
            stopCamera();
            // We don't revoke storedPhoto here to avoid issues if it was already revoked or being used elsewhere
        };
    }, [show]);

    // Conditional camera activation: Only start if modal is open and initial check is finished
    useEffect(() => {
        const shouldBeRunning = show && initialFetchDone && !isLoadingPhoto && !storedPhoto && captureMode === "webcam";

        if (shouldBeRunning) {
            startCamera();
        } else {
            stopCamera();
        }
    }, [show, initialFetchDone, isLoadingPhoto, storedPhoto, captureMode]);

    useEffect(() => {
        if (show && captureMode === "device" && !isLoadingPhoto) {
            fetchDevices();
        }
    }, [show, captureMode, isLoadingPhoto]);

    // Attach stream to video element when it mounts or stream changes
    useEffect(() => {
        if (show && stream && videoRef.current && !storedPhoto && captureMode === "webcam") {
            // Use requestAnimationFrame to ensure the video element is truly ready in the DOM
            const frameId = requestAnimationFrame(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            });
            return () => cancelAnimationFrame(frameId);
        }
    }, [stream, show, storedPhoto, captureMode, initialFetchDone]);

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="sm"
            className="biometric-capture-modal"
            backdrop="static"
        >
            <Modal.Header>
                <div className="d-flex align-items-center gap-3 w-100">
                    <button className="biometric-back-btn" onClick={onClose}>
                        <i className="bi bi-chevron-left"></i>
                    </button>
                    <div className="biometric-capture-header">
                        <span className="biometric-capture-title">Registro Biométrico Facial</span>
                        <span className="biometric-capture-subtitle">
                            Usuario: <strong>{user?.customer_name || 'N/A'}</strong>
                        </span>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-4">
                {error && (
                    <Alert variant="danger" className="rounded-3 mb-4" onClose={() => setError(null)} dismissible>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {error}
                    </Alert>
                )}

                {!showFullHistory && !storedPhoto && !isLoadingPhoto && (
                    <div className="biometric-mode-selector mb-4">
                        <button
                            className={`mode-btn ${captureMode === 'webcam' ? 'active' : ''}`}
                            onClick={() => setCaptureMode('webcam')}
                        >
                            <i className="bi bi-camera-video"></i>
                            Cámara Web
                        </button>
                        <button
                            className={`mode-btn ${captureMode === 'device' ? 'active' : ''}`}
                            onClick={() => setCaptureMode('device')}
                        >
                            <i className="bi bi-cpu"></i>
                            Dispositivo
                        </button>
                    </div>
                )}

                {!showFullHistory && (
                    <div className="biometric-video-container">
                        {isLoadingPhoto ? (
                            <div className="biometric-skeleton">
                                <div className="skeleton-pulse-overlay"></div>
                                <div className="skeleton-content">
                                    <i className="bi bi-person-bounding-box skeleton-icon"></i>
                                    <span className="skeleton-text">Cargando biometría...</span>
                                </div>
                            </div>
                        ) : storedPhoto ? (
                            <div className="biometric-photo-view">
                                <img src={storedPhoto} alt="Current" className="biometric-stored-img-full" />
                                <div className="biometric-verified-overlay">
                                    <div className="status-label-minimal">
                                        <i className="bi bi-shield-check"></i>
                                        Rostro Registrado
                                    </div>
                                </div>
                            </div>
                        ) : captureMode === 'device' ? (
                            <div className="biometric-device-view">
                                <div className="device-selection-container p-4">
                                    <span className="device-selection-label">Seleccionar lector biométrico:</span>
                                    {isFetchingDevices ? (
                                        <div className="text-center py-2">
                                            <div className="spinner-border spinner-border-sm text-light" />
                                        </div>
                                    ) : (
                                        <select
                                            className="form-select biometric-device-select mt-2"
                                            value={selectedDeviceId}
                                            onChange={(e) => setSelectedDeviceId(e.target.value)}
                                        >
                                            <option value="">Seleccione un equipo...</option>
                                            {devices.map(dev => (
                                                <option key={dev.id} value={dev.id}>
                                                    {dev.name} ({dev.location?.name || 'Sin locación'})
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <div className="device-instructions mt-3">
                                        <i className="bi bi-info-circle me-2"></i>
                                        Asegúrese de que el equipo esté conectado y el usuario esté frente a él.
                                    </div>
                                </div>
                            </div>
                        ) : isCameraLoading ? (
                            <div className="biometric-skeleton">
                                <div className="skeleton-pulse-overlay"></div>
                                <div className="skeleton-content">
                                    <i className="bi bi-camera skeleton-icon"></i>
                                    <span className="skeleton-text">Iniciando cámara...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    className="biometric-video"
                                />
                                <div className="biometric-capture-overlay"></div>
                            </>
                        )}
                    </div>
                )}

                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {!showFullHistory && (
                    <div className="biometric-controls">
                        {storedPhoto ? (
                            <div className="d-flex flex-column gap-2 w-100">
                                <button
                                    className="btn-update-photo-full"
                                    onClick={() => {
                                        if (storedPhoto && storedPhoto.startsWith('blob:')) {
                                            URL.revokeObjectURL(storedPhoto);
                                        }
                                        setStoredPhoto(null);
                                    }}
                                >
                                    <i className="bi bi-camera"></i> Actualizar Biometría
                                </button>
                                <button
                                    className="btn-delete-biometric"
                                    onClick={confirmDelete}
                                >
                                    <i className="bi bi-trash"></i> Eliminar Registro
                                </button>
                            </div>
                        ) : captureMode === 'webcam' ? (
                            <button
                                className="btn-capture-full"
                                onClick={capturePhoto}
                                disabled={isCapturing || !stream}
                            >
                                {isCapturing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Capturando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-camera-fill"></i> Capturar Rostro
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                className="btn-capture-full"
                                onClick={handleDeviceSnapshot}
                                disabled={isCapturing || !selectedDeviceId}
                            >
                                {isCapturing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" />
                                        Enviando orden...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-cpu-fill"></i> Capturar desde Dispositivo
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}

                {!showFullHistory && (
                    <div className="registered-locations-section mt-4">
                        <div className="section-title mb-3">
                            <i className="bi bi-geo-alt-fill me-2"></i>
                            Locaciones Registradas
                        </div>
                        {isLoadingLocations ? (
                            <div className="text-center py-2">
                                <span className="spinner-border spinner-border-sm text-secondary"></span>
                            </div>
                        ) : registeredLocations.length > 0 ? (
                            <div className="locations-grid">
                                {registeredLocations.map(loc => (
                                    <div key={loc.id} className="location-item animate-fade-in">
                                        <div className="location-info">
                                            <span className="location-name">{loc.name}</span>
                                            {loc.city && <span className="location-city">{loc.city}</span>}
                                        </div>
                                        <button
                                            className="location-delete-btn"
                                            title="Remover de esta locación"
                                            onClick={() => confirmDeleteFromLocation(loc)}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-locations-msg">
                                El usuario no tiene biometría sincronizada en locaciones.
                            </div>
                        )}
                    </div>
                )}

                <div className={`history-section ${showFullHistory ? 'full-view' : ''}`}>
                    <div className="history-title-container">
                        <div className="history-title">
                            <i className="bi bi-clock-history"></i>
                            {showFullHistory ? 'Logs de Ingreso Completos' : 'Últimos Logs de Ingreso'}
                        </div>
                        {history.length > 3 && (
                            <button
                                className="btn-history-toggle"
                                onClick={() => setShowFullHistory(!showFullHistory)}
                            >
                                {showFullHistory ? (
                                    <><i className="bi bi-chevron-left me-1"></i> <span>Volver</span></>
                                ) : (
                                    <><span>Ver más</span> <i className="bi bi-chevron-right ms-1"></i></>
                                )}
                            </button>
                        )}
                    </div>

                    <div className="history-list">
                        {isLoadingHistory ? (
                            <div className="text-center py-3">
                                <div className="spinner-border spinner-border-sm text-secondary" />
                            </div>
                        ) : history.length > 0 ? (
                            (showFullHistory ? history : history.slice(0, 3)).map(item => (
                                <div key={item.id} className="history-item">
                                    <div className="history-item-info">
                                        <div className="history-item-thumb">
                                            {item.thumb ? (
                                                <img src={item.thumb} alt="Biometric" />
                                            ) : (
                                                <div className={`history-status-indicator ${item.is_allowed ? 'allowed' : 'denied'}`}>
                                                    <i className={`bi ${item.is_allowed ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                                                </div>
                                            )}
                                        </div>
                                        <div className="history-item-details">
                                            <span className="history-item-date">{item.date}</span>
                                            <span className="history-item-res res-valid">
                                                <i className="bi bi-geo-alt-fill me-1"></i>
                                                {item.location || 'Ubicación desconocida'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="history-item-status">
                                        <span className={`badge ${item.is_allowed ? 'badge-allowed' : 'badge-denied'} px-2 py-1`}>
                                            {item.status}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4 text-muted small">
                                No hay registros previos para este usuario.
                            </div>
                        )}
                    </div>
                </div>

                {showInfoModal && (
                    <InfoModal
                        title={infoModalData.title}
                        description={infoModalData.description}
                        iconType={infoModalData.iconType}
                        primaryAction={infoModalData.primaryAction}
                        secondaryAction={infoModalData.secondaryAction}
                    />
                )}
            </Modal.Body>
        </Modal>
    );
};

export default BiometricCaptureModal;
